<template>
  <div class="global-search">
    <div class="p-[0.625rem]">
      <div class="global-search__filters flex flex-col gap-3">
        <search-filter
          :value="searchTerm"
          :options="allTags"
          :selected="selectedTagNames"
          :show-select-all="false"
          :hide-tags="true"
          class="global-search__searchFilter"
          data-test="producer-globalSearch-searchFilter"
          @update:value="onSearchChange"
          @update:selected="onTagsChange"
        />

        <div class="global-search__quickFilters flex flex-wrap gap-1" data-test="producer-globalSearch-quickFilters">
          <div
            v-for="chip in quickFilters"
            :key="chip.value || 'all'"
            class="global-search__quickFilterWrap"
            :class="chip.hint ? 'global-search__quickFilter--has-hint' : ''"
          >
            <button
              type="button"
              class="global-search__quickFilter inline-flex items-center rounded-full border border-transparent bg-white/[0.04] px-2 py-[0.375rem] text-[12px] leading-none text-white/70 transition cursor-pointer hover:text-white"
              :class="[
                isActiveQuickFilter(chip.value) ? 'global-search__quickFilter--active border-white/20 bg-white/[0.07] text-white/90 font-bold' : 'font-medium',
              ]"
              :aria-pressed="isActiveQuickFilter(chip.value)"
              :data-test="`producer-globalSearch-quickFilter:${chip.value || 'all'}`"
              @click="selectQuickFilter(chip.value)"
            >
              {{ chip.label }}
            </button>

            <hint-trigger
              v-if="chip.hint"
              :text="chip.hint"
              class="global-search__quickFilter-hint"
              tabindex="0"
              role="img"
              :aria-label="chip.hint"
              @click.stop
            >
              <i class="fas fa-circle-info" />
            </hint-trigger>
          </div>
        </div>

        <template v-if="selectedTagNames.length">
          <hr class="global-search__tagsDivider m-0 w-full border-0 border-t border-t-white/10">

          <div class="global-search__tags flex flex-wrap gap-2" data-test="producer-globalSearch-tags">
            <span
              v-for="tag in selectedTagNames"
              :key="tag"
              class="global-search__tag inline-flex items-center gap-2 rounded-full bg-producer-slate-chip px-2 py-1 text-[10px]"
              :data-test="`producer-globalSearch-tag:${tag}`"
            >
              {{ tag }}
              <button
                type="button"
                class="global-search__tagClose flex h-3 w-3 items-center justify-center rounded-full border-0 bg-transparent p-0 leading-none cursor-pointer"
                :aria-label="`Remove ${tag}`"
                :data-test="`producer-globalSearch-tagClose:${tag}`"
                @click="removeTag(tag)"
              >
                <span class="fa fa-times text-[8px]" />
              </button>
            </span>
          </div>
        </template>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading && !results.length" class="py-8 text-center" data-test="producer-globalSearch-loading">
      <div class="loading loading--inline m-0">
        <i class="fas fa-spinner fa-spin"/>
      </div>
      <p class="mt-2 text-[13px] text-white/50">Searching...</p>
    </div>

    <!-- No results after searching/filtering -->
    <div v-if="!isLoading && !total && (searchTerm || selectedTagNames.length || hasNonDefaultResourceTypes)" class="py-8 text-center" data-test="producer-globalSearch-noResults">
      <h3 class="mb-4 text-[14px] font-semibold">No results found</h3>

      <button
        class="tb-btn tb-btn-contained medium-em px-3"
        data-test="producer-globalSearch-resetSearch"
        @click="resetAll"
      >
        Reset Search
      </button>
    </div>

    <!-- Empty state (no search yet, no filters beyond defaults) -->
    <div v-if="!isLoading && !total && !searchTerm && !selectedTagNames.length && !hasNonDefaultResourceTypes" class="py-8 text-center" data-test="producer-globalSearch-empty">
      <h3 class="mb-4 text-[14px] font-semibold text-white/50">Search across all assets</h3>
      <p class="text-[12px] text-white/30">Type to search or select tags to find assets</p>
    </div>

    <!-- Results list -->
    <div
      v-show="results.length"
      ref="sourceList"
      class="source-list"
      data-test="producer-globalSearch-list"
    >
      <template v-for="item in results">
        <!-- Non-poll item: individual draggable -->
        <draggable
          v-if="item.resourceType !== 'poll'"
          :key="item.resourceId"
          :list="[item]"
          :clone="clone"
          :sort="false"
          animation="200"
          :group="{ name: 'timeline', pull: 'clone', put: false }"
          @start="startDrag"
          @end="onDragEnd"
          revert-on-spill="true"
        >
          <div
            class="source-item"
            :class="[`source-item--${item.resourceType}`]"
            :data-template-id="item.resourceId"
            :data-template-id-type="typeof item.resourceId"
            :data-component-type="item.resourceType"
            data-entity="producer-globalSearch-result"
            :data-test="`producer-globalSearch-element:${item.resourceId}`"
          >
            <div class="tb-checkbox">
              <input
                type="checkbox"
                :id="`gs-${item.resourceId}`"
                :value="item"
                v-model="selected"
                data-entity="producer-globalSearch-toggleSelection"
                :data-test="`producer-globalSearch-element-checkBox:${item.resourceId}`"
              />
              <label :for="`gs-${item.resourceId}`" />
            </div>

            <div class="source-item-wrapper">
              <img
                v-if="item.previewUrl"
                class="preview mr-2"
                :src="item.previewUrl"
                :data-test="`producer-globalSearch-element-preview:${item.resourceId}`"
              />

              <div class="source-item-info-wrapper min-w-0 flex-1">
                <div
                  class="source-title title-text"
                  :title="item.title"
                  :data-test="`producer-globalSearch-element-title:${item.resourceId}`"
                >
                  {{ item.title }}
                </div>

                <tag-list
                  v-if="item.tags?.length"
                  :tags="item.tags"
                  :data-test="`producer-globalSearch-element-tags:${item.resourceId}`"
                />
              </div>

              <div class="source-description" :data-test="`producer-globalSearch-element-type:${item.resourceId}`">
                <span class="resource-type-label" :class="`resource-type-label--${item.resourceType}`">
                  {{ formatResourceTypeLabel(item) }}
                </span>
              </div>

              <div class="source-actions ml-1">
                <button
                  class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                  title="Add to playlist"
                  data-entity="producer-globalSearch-addToPlaylist"
                  :data-test="`producer-globalSearch-element-addToPlaylist:${item.resourceId}`"
                  @click="addToPlaylist(item, addToPlaylistType(item.resourceType))"
                >
                  <span class="fas fa-plus" />
                </button>
              </div>
            </div>
          </div>
        </draggable>

        <!-- Poll item: collapsible accordion section -->
        <div v-else :key="item.resourceId" class="poll-section" :data-test="`producer-globalSearch-poll:${item.resourceId}`">
          <div class="poll-header" @click="togglePollExpand(item.resourceId)">
            <button
              class="poll-toggle border-0 bg-transparent p-0 leading-none cursor-pointer text-[#a5a5a5] hover:text-white"
              :data-test="`producer-globalSearch-poll-toggle:${item.resourceId}`"
              :aria-expanded="expandedPolls[item.resourceId] || false"
              aria-label="Toggle poll displays"
              @click.stop="togglePollExpand(item.resourceId)"
            >
              <span
                class="fas fa-chevron-right poll-chevron block text-[14px] leading-none"
                :class="{ 'poll-chevron--open': expandedPolls[item.resourceId] }"
              />
            </button>

            <div class="source-item-wrapper">
              <div class="source-item-info-wrapper min-w-0 flex-1">
                <div
                  class="source-title title-text"
                  :title="item.title"
                  :data-test="`producer-globalSearch-poll-title:${item.resourceId}`"
                >
                  {{ item.title }}
                </div>

                <tag-list
                  v-if="item.tags?.length"
                  :tags="item.tags"
                  :data-test="`producer-globalSearch-poll-tags:${item.resourceId}`"
                />
              </div>

              <div class="source-description" :data-test="`producer-globalSearch-poll-type:${item.resourceId}`">
                <span class="resource-type-label resource-type-label--poll">
                  poll
                </span>
              </div>
            </div>
          </div>

          <!-- Expanded poll displays -->
          <div
            v-if="expandedPolls[item.resourceId]"
            class="poll-displays"
            :data-test="`producer-globalSearch-poll-displays:${item.resourceId}`"
          >
            <div
              v-if="pollDisplaysLoading[item.resourceId]"
              class="flex items-center justify-center py-4"
              :data-test="`producer-globalSearch-poll-displays-loading:${item.resourceId}`"
            >
              <div class="loading loading--inline m-0">
                <i class="fas fa-spinner fa-spin" />
              </div>
            </div>

            <div
              v-else-if="!pollDisplays[item.resourceId]?.length"
              class="text-center text-[12px] text-white/30 mb-4"
              :data-test="`producer-globalSearch-poll-displays-empty:${item.resourceId}`"
            >
              <em>
                Add your first <strong>On Screen Layout</strong> in Creator to start showing this poll and results.
              </em>
            </div>

            <draggable
              v-else
              :list="pollDisplays[item.resourceId]"
              :clone="clone"
              :sort="false"
              animation="200"
              :group="{ name: 'timeline', pull: 'clone', put: false }"
              @start="startPollDisplayDrag"
              @end="onDragEnd"
              revert-on-spill="true"
            >
              <div
                v-for="display in pollDisplays[item.resourceId]"
                :key="display.resourceId"
                class="source-item source-item--display"
                :data-template-id="display.resourceId"
                :data-template-id-type="typeof display.resourceId"
                :data-component-type="display.resourceType"
                :data-test="`producer-globalSearch-poll-display:${display.resourceId}`"
              >
                <div class="tb-checkbox">
                  <input
                    type="checkbox"
                    :id="`gs-display-${display.resourceId}`"
                    :value="display"
                    v-model="selected"
                    :data-test="`producer-globalSearch-poll-display-checkBox:${display.resourceId}`"
                  />
                  <label :for="`gs-display-${display.resourceId}`" />
                </div>

                <div class="source-item-wrapper">
                  <img
                    v-if="display.previewUrl"
                    class="preview mr-2"
                    :src="display.previewUrl"
                    :data-test="`producer-globalSearch-poll-display-preview:${display.resourceId}`"
                  />

                  <div class="source-item-info-wrapper min-w-0 flex-1">
                    <div
                      class="source-title title-text"
                      :title="display.title"
                      :data-test="`producer-globalSearch-poll-display-title:${display.resourceId}`"
                    >
                      {{ display.title }}
                    </div>
                  </div>

                  <div class="source-description" :data-test="`producer-globalSearch-poll-display-type:${display.resourceId}`">
                    <span class="resource-type-label resource-type-label--display">
                      display
                    </span>
                  </div>

                  <div class="source-actions ml-1">
                    <button
                      class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
                      title="Add to playlist"
                      data-entity="producer-globalSearch-addToPlaylist"
                      :data-test="`producer-globalSearch-poll-display-add:${display.resourceId}`"
                      @click="addToPlaylist(display, 'poll')"
                    >
                      <span class="fas fa-plus" />
                    </button>
                  </div>
                </div>
              </div>
            </draggable>
          </div>
        </div>
      </template>

      <div ref="loadTrigger" class="load-trigger flex min-h-16 w-full items-center justify-center">
        <div v-if="isLoading && results.length" class="loading loading--inline m-0" data-test="producer-globalSearch-loadingMore">
          <i class="fas fa-spinner fa-spin" />
        </div>
      </div>
    </div>

    <!-- Selected actions bar -->
    <div
      v-if="selected.length"
      class="producer-assets-selected flex items-center justify-end p-4"
      data-test="producer-globalSearch-selected"
    >
      <p
        class="producer-assets-selected__count mb-0 mr-4 mt-0 text-primary"
        data-test="producer-globalSearch-selected-count"
        role="status"
        aria-live="polite"
      >
        {{ selected.length }} selected
      </p>

      <button
        ref="addToPlaylistBtn"
        class="tb-btn tb-btn-contained medium-em"
        :class="{ disabled: !selected.length }"
        :disabled="!selected.length"
        data-entity="producer-globalSearch-addSelectedToPlaylist"
        data-test="producer-globalSearch-addSelectedToPlaylist"
        @click="addSelectedToPlaylist"
      >
        Add to playlist
      </button>
    </div>
  </div>
</template>

<script>
import { pick, uniqBy } from 'lodash-es';
import * as uuid from 'uuid';

import { mapState, mapGetters } from 'vuex';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import TagList from '@/js/shared/TagList.vue';
import HintTrigger from '@/js/shared/HintTrigger.vue';
import { parseTotal, maxPage as maxPageFor } from '@/js/utils/search-pagination';
import * as globalSearchService from '../services/global-search';
import tabMixins from '../mixins/tab-mixins';

const RESOURCE_TYPE_OPTIONS = [
  { value: 'graphic', label: 'Custom' },
  { value: 'panel', label: 'Classic' },
  { value: 'theme', label: 'Social' },
  { value: 'poll', label: 'Poll' },
];

const QUICK_FILTERS = [
  { value: null, label: 'All' },
  { value: 'graphic', label: 'Custom', hint: 'Previously called Graphics' },
  { value: 'theme', label: 'Social' },
  { value: 'poll', label: 'Polls' },
  { value: 'panel', label: 'Classic', hint: 'e.g. Countdown Clock, legacy polls' },
];

/**
 * Displays with displayType === 'poll' are interactive voting widgets
 * that cannot be added to the timeline. Filter them out.
 */
const isInteractiveDisplay = (item) => (
  item.resourceSubType === 'poll'
);

export default {
  name: 'GlobalSearch',

  components: {
    SearchFilter,
    TagList,
    HintTrigger,
  },

  mixins: [tabMixins],

  data() {
    return {
      results: [],
      total: 0,
      page: 1,
      limit: 100,
      isLoading: false,
      selected: [],
      requestId: 0,
      hasMore: true,

      // Poll nesting state
      expandedPolls: {},
      pollDisplays: {},
      pollDisplaysLoading: {},
    };
  },

  computed: {
    ...mapState('search', [
      'searchTerm',
      'selectedTagNames',
      'allTags',
      'selectedResourceTypes',
    ]),

    ...mapGetters('search', [
      'effectiveResourceTypes',
      'hasNonDefaultResourceTypes',
    ]),

    quickFilters() {
      return QUICK_FILTERS;
    },

    /**
     * Highest page number that can be requested, derived from the total
     * result count reported by the API. 0 when there is nothing to page.
     */
    maxPage() {
      return maxPageFor(this.total, this.limit);
    },
  },

  created() {
    this.fetchTags();
    this.fetchResults();
  },

  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  beforeUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  },

  watch: {
    results() {
      this.$nextTick(() => {
        if (
          this.$refs.sourceList
          && this.results.length
          && this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight
        ) {
          this.loadMore();
        }
      });
    },

    // Move focus to the bulk action bar as soon as it appears, so keyboard
    // users don't have to tab through the rest of the list to reach it.
    selected(newVal, oldVal) {
      if (oldVal.length === 0 && newVal.length > 0) {
        this.$nextTick(() => this.$refs.addToPlaylistBtn?.focus());
      }
    },
  },

  methods: {
    async fetchTags() {
      try {
        const response = await globalSearchService.getTags({
          resourceTypes: this.effectiveResourceTypes,
        });
        this.$store.commit('search/SET_ALL_TAGS', (response.tags || []).sort());
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    },

    async fetchResults(opts = {}) {
      const id = this.requestId + 1;
      this.requestId = id;
      this.isLoading = true;

      try {
        const response = await globalSearchService.search({
          search: this.searchTerm,
          resourceTypes: this.effectiveResourceTypes,
          tags: this.selectedTagNames,
          page: opts.reset ? 1 : this.page,
          limit: this.limit,
        });

        // Ignore stale responses from earlier requests
        if (id !== this.requestId) {
          return;
        }

        const newResults = response.results || [];
        // Parse the total strictly: a bad/missing value must never allow the
        // pagination guards below to keep issuing requests indefinitely.
        this.total = parseTotal(response.total);

        if (opts.reset) {
          this.results = newResults;
          this.page = 2;
          this.hasMore = true;
        } else {
          const previousCount = this.results.length;
          this.results = uniqBy(
            [...this.results, ...newResults],
            // Key on resource type + id: ids are only unique within a
            // resource type (e.g. polls and displays share numeric ids), so
            // keying on `resourceId` alone can collapse distinct items and
            // undercount `results.length` — which keeps the `loadMore` guard
            // below from ever terminating.
            (item) => `${item.resourceType}:${item.resourceId}`,
          );

          // A page that added no new unique items means the API has run out
          // of results (or keeps returning duplicates). Stop paginating
          // instead of issuing an endless stream of empty requests.
          if (this.results.length === previousCount) {
            this.hasMore = false;
          }

          this.page += 1;
        }
      } catch (err) {
        // Only update error state for the latest request
        if (id === this.requestId) {
          console.error('Failed to search:', err);
          this.total = 0;
          this.isLoading = false;
        }
      } finally {
        // Only clear loading if no newer request is in flight
        if (id === this.requestId) {
          this.isLoading = false;
        }
      }
    },

    onSearchChange(value) {
      this.$store.commit('search/SET_SEARCH_TERM', value);
      this.resetAndFetch();
    },

    onTagsChange(tags) {
      this.$store.commit('search/SET_SELECTED_TAG_NAMES', tags);
      this.resetAndFetch();
    },

    removeTag(tag) {
      this.$store.commit('search/REMOVE_TAG', tag);
      this.resetAndFetch();
    },

    selectQuickFilter(value) {
      this.$store.commit('search/SET_SELECTED_RESOURCE_TYPES', value ? [value] : []);
      this.onFiltersChanged();
    },

    isActiveQuickFilter(value) {
      if (!value) {
        return !this.selectedResourceTypes.length;
      }

      return this.selectedResourceTypes.length === 1 && this.selectedResourceTypes[0] === value;
    },

    onFiltersChanged() {
      this.fetchTags();
      this.resetAndFetch();
    },

    resetAll() {
      this.$store.commit('search/RESET_FILTERS');
      this.fetchTags();
      this.resetAndFetch();
    },

    resetAndFetch() {
      this.selected = [];
      this.results = [];
      this.total = 0;
      this.page = 1;
      this.hasMore = true;
      this.expandedPolls = {};
      this.pollDisplays = {};
      this.pollDisplaysLoading = {};
      this.fetchResults({ reset: true });
    },

    refresh() {
      this.fetchTags();
      this.resetAndFetch();
    },

    loadMore() {
      if (this.isLoading || !this.hasMore) {
        return;
      }

      // Nothing left to fetch: we have every result the API reported.
      if (this.total <= this.results.length) {
        return;
      }

      // Never request past the last page implied by the reported total.
      if (this.page > this.maxPage) {
        return;
      }

      this.fetchResults();
    },

    resourceTypeLabel(item) {
      const option = RESOURCE_TYPE_OPTIONS.find((rt) => rt.value === item.resourceType);
      return option ? option.label : item.resourceType;
    },

    formatResourceTypeLabel(item) {
      const label = this.resourceTypeLabel(item).toLowerCase();
      const words = label.split(' ');

      return words.length === 2 ? `${words[0]}\n${words[1]}` : label;
    },

    isInteractiveDisplay,

    async togglePollExpand(pollId) {
      if (this.expandedPolls[pollId]) {
        this.expandedPolls[pollId] = !this.expandedPolls[pollId];
        return;
      }

      this.expandedPolls[pollId] = true;
      this.pollDisplaysLoading[pollId] = true;

      try {
        const response = await globalSearchService.search({
          resourceTypes: ['display'],
          parentResourceId: pollId,
          page: 1,
          limit: 100,
        });

        this.pollDisplays[pollId] = (response.results || []).filter(
          (d) => !isInteractiveDisplay(d),
        );
      } catch (err) {
        console.error('Failed to load poll displays:', err);
        this.pollDisplays[pollId] = [];
      } finally {
        this.pollDisplaysLoading[pollId] = false;
      }
    },

    addToPlaylistType(resourceType) {
      switch (resourceType) {
        case 'graphic':
          return 'Graphic';
        case 'panel':
          return 'Panel';
        case 'poll':
        case 'display':
          return 'poll';
        case 'theme':
        default:
          return 'Theme';
      }
    },

    selectedPlaylistType() {
      // Determine the type for "add selected to playlist" based on selected items
      const types = [...new Set(this.selected.map((item) => item.resourceType))];

      if (types.length === 1) {
        return this.addToPlaylistType(types[0]);
      }

      // Mixed types — default to themes as the generic type
      return 'Theme';
    },

    async addSelectedToPlaylist() {
      const items = this.selected.map((item) => this.clone(item));

      await this.$store.dispatch('addLayouts', { items });

      // Ensure lookup tables are populated with any missing metadata for timeline items
      await this.$store.dispatch('getTimelineItems');
      this.$store.dispatch('updateTimeline');

      this.selected = [];
    },

    startDrag(e) {
      const { item } = e;

      // Since not all resources have number IDs, we have to check the type ahead of time.
      const { templateId, templateIdType } = item.dataset;
      const resourceId = templateIdType === 'number' ? parseInt(templateId, 10) : templateId;

      const draggedItem = this.results.find((t) => t.resourceId === resourceId);
      if (!draggedItem) {
        return;
      }

      // Determine drag type from the item being dragged
      let dragType = 'graphic';
      switch (draggedItem.resourceType) {
        case 'panel':
          dragType = 'panel';
          break;
        case 'theme':
        case 'display':
          dragType = 'theme';
          break;
        case 'poll':
          dragType = 'poll';
          break;
        default:
          break;
      }

      // Pass all selected items as a group when the dragged item is selected
      const grabbed = this.selected.find((t) => t.resourceId === resourceId);
      let dragItems = null;
      if (grabbed) {
        dragItems = this.selected.map(this.clone);
      }

      this.$store.dispatch('setDragType', {
        type: dragType,
        group: dragItems,
      });
    },

    startPollDisplayDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabbed = this.selected.find((t) => t.resourceId === parseInt(templateId, 10));
      const dragItems = grabbed ? this.selected.map(this.clone) : null;

      this.$store.dispatch('setDragType', {
        type: 'poll',
        group: dragItems,
      });
    },

    onDragEnd() {
      this.$store.dispatch('clearDragType');
    },

    clone(original) {
      const { resourceType, resourceId } = original;

      const base = {
        timeline_id: uuid.v1(),
        displayTime: resourceType === 'theme' ? 30000 : 5000,
        transition: 'Cut',
        posts: [],
        fields: {},
      };

      switch (resourceType) {
        case 'graphic': {
          const clone = pick(original, ['title', 'owner', 'meta']);
          clone.template_id = resourceId;
          clone.timeline_id = base.timeline_id;
          clone.displayTime = base.displayTime;
          clone.transition = base.transition;
          clone.layout = 'graphic';
          clone.posts = [{ timeline_id: clone.timeline_id }];
          clone.fields = {};

          if (original.meta) {
            clone.meta = original.meta;
          }

          // Carry the thumbnail so the playlist can render immediately
          // while the full template metadata loads.
          if (original.previewUrl) {
            clone.previewUrl = original.previewUrl;
          }

          return clone;
        }

        case 'panel': {
          const clone = pick(original, ['type', 'title']);
          clone._id = resourceId;
          clone.timeline_id = base.timeline_id;
          clone.displayTime = base.displayTime;
          clone.transition = base.transition;
          clone.layout = 'panel';
          clone.posts = [{ timeline_id: clone.timeline_id }];
          clone.fields = {};

          // Carry the thumbnail so the playlist can render immediately
          // while the full panel metadata loads.
          if (original.previewUrl) {
            clone.previewUrl = original.previewUrl;
          }

          return clone;
        }

        case 'theme': {
          const clone = pick(original, ['title']);
          clone._id = resourceId;
          clone.layout = original.resourceSubType || 'theme';
          clone.posts = [];
          clone.fields = {};
          clone.timeline_id = base.timeline_id;
          clone.displayTime = base.displayTime;
          clone.transition = base.transition;
          clone.queuedPost = null;

          // Carry the thumbnail so the playlist can render immediately
          // while the full theme metadata loads.
          if (original.previewUrl) {
            clone.previewUrl = original.previewUrl;
          }

          return clone;
        }

        case 'poll':
        case 'display': {
          const clone = pick(original, ['title', 'displayType', 'previewUrl']);
          clone.pollId = original.parentResourceId || resourceId;
          clone.displayId = resourceId;
          clone.layout = 'poll';
          clone.posts = [];
          clone.fields = {};
          clone.timeline_id = base.timeline_id;
          clone.displayTime = base.displayTime;
          clone.transition = base.transition;
          return clone;
        }

        default: {
          const clone = pick(original, ['title']);
          clone.timeline_id = base.timeline_id;
          clone.displayTime = base.displayTime;
          clone.transition = base.transition;
          clone.layout = 'graphic';
          clone.posts = [{ timeline_id: clone.timeline_id }];
          clone.fields = {};
          return clone;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.global-search {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.global-search__searchFilter {
  flex: 1;
  min-width: 0;
}

.global-search__filters .searchFilter__selectAll {
  display: none;
}

.source-description {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 80px;
  flex-shrink: 0;
}

.global-search__quickFilter-hint {
  position: absolute;
  top: -5px;
  right: -5px;
  font-size: 9px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.5);
  cursor: help;
  pointer-events: all;

  &:hover,
  &:focus {
    color: rgba(255, 255, 255, 0.9);
    outline: none;
  }
}

.global-search__quickFilter--has-hint {
  position: relative;
}

.global-search__quickFilterWrap.global-search__quickFilter--has-hint .global-search__quickFilter {
  padding-right: 0.625rem;
}

.resource-type-label {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  font-style: normal;
  text-transform: capitalize;
  letter-spacing: 0.5px;
  white-space: pre-line;
  text-align: right;
  line-height: 1;

  &--graphic {
    color: #0095a9;
  }

  &--panel {
    color: #607d8b;
  }

  &--theme {
    color: #009688;
  }

  &--poll {
    color: #A30065;
  }

  &--display {
    color: #A30065;
  }

  &--page {
    color: #f9a8d4;
  }

  &--asset {
    color: #fde68a;
  }

  &--tagboard {
    color: #c7d2fe;
  }
}

.poll-section {
  margin-bottom: .5vmin;
  background: rgba(255, 255, 255, 0.01);
  border-radius: 6px;
  padding-right: 5px;
}

.poll-header {
  display: flex;
  align-items: center;
  width: 100%;
  border: none;
  padding: 0;
  text-align: left;
  overflow: visible;
  background: transparent;
  font-size: 0.75rem;
  line-height: 1.4;
  cursor: pointer;
  height: 50px;

  .source-item-wrapper {
    display: flex;
    align-items: center;
    flex-grow: 1;
    min-height: 0;
    padding: 8px;
    background: transparent;
    border-radius: 6px;
    overflow: hidden;
  }
}

.poll-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  min-width: 36px;
  height: 100%;
  min-height: 40px;
  margin-left: -8px;
  color: #393939;
}

.poll-chevron {
  transition: transform 0.2s ease;
}

.poll-chevron--open {
  transform: rotate(90deg);
}

.poll-displays {
  padding-left: 28px;
  padding-top: 4px;
  padding-bottom: 4px;
}

.source-item .source-title,
.poll-header .source-title {
  color: #FFF;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.source-item .preview {
  display: block;
  height: 100%;
  padding: 2px;
  object-fit: cover;
  aspect-ratio: 16 / 9;
  border-radius: 2px;
  flex-shrink: 0;
}

.source-item .source-item-wrapper {
  padding: 5px 8px;
  height: 45px;
  border-radius: 6px;
  background: #2C2C2C;
}

.poll-header .source-item-wrapper {
  padding: 8px;
}
</style>
